"use client";

import { useState, useEffect, useMemo } from "react";
import { FaChevronLeft } from "react-icons/fa";
import {
  TrashIcon,
  PlusIcon,
  PencilIcon,
  MoreHorizontalIcon,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/shadcn-components/ui/button";
import { Alert, AlertDescription } from "@/shadcn-components/ui/alert";
import { useAccountsStore, getGatewayLabel } from "@/lib/accounts/store";
import {
  useSyncedAccountsStore,
  selectUnmatchedSynced,
  type SyncedAccountView,
} from "@/lib/accounts/syncedAccounts";
import { TradingAccount } from "@/lib/accounts/types";
import { gatewayLogoUrl } from "@/lib/accounts/brokerLogo";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress/stores/useConnectionProgressStore";
import { BrokerLogo, GatewayChip, useSwipeActions } from "@/components/common";
import {
  AccountDetailsScreen,
  AddAccountContent,
  EditAccountContent,
} from "@/components/account-information";

interface AccountSelectorScreenProps {
  onBack: () => void;
  /** Open the add-account flow immediately when the screen mounts. */
  initialAddAccount?: boolean;
}

const ACTIONS_WIDTH = 96;

function AccountRow({
  account,
  isActive,
  isConnected,
  onSelect,
  onEdit,
  onDelete,
}: {
  account: TradingAccount;
  isActive: boolean;
  isConnected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const swipe = useSwipeActions(ACTIONS_WIDTH);
  const trayVisible = swipe.open || swipe.offset !== 0;

  return (
    <div className="relative rounded-xl overflow-hidden">
      {/* Row actions tray — only in the layer while swiping/open, so it can never
          show through the (opaque) card at rest */}
      <div
        className={`absolute inset-y-0 right-0 flex items-stretch ${trayVisible ? "" : "hidden"}`}
        style={{ width: ACTIONS_WIDTH }}
        aria-hidden={!swipe.open}
      >
        <button
          type="button"
          tabIndex={swipe.open ? 0 : -1}
          onClick={() => {
            swipe.close();
            onEdit();
          }}
          className="flex-1 flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted"
          aria-label="Edit account"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          tabIndex={swipe.open ? 0 : -1}
          onClick={() => {
            swipe.close();
            onDelete();
          }}
          className="flex-1 flex items-center justify-center text-red-400 bg-muted hover:bg-red-500/10"
          aria-label="Delete account"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Swipeable card — Add-flow company-card idiom (opaque bg-card) */}
      <div
        {...swipe.handlers}
        className={`relative flex items-center gap-3 p-3 rounded-xl border bg-card cursor-pointer touch-pan-y transition-transform duration-150 hover:border-primary/50 ${
          isActive ? "border-blue-400/60 border-l-2 border-l-blue-400" : "border-border"
        }`}
        style={{ transform: `translateX(${swipe.offset}px)` }}
        onClick={() => {
          if (swipe.open) {
            swipe.close();
            return;
          }
          onSelect();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <BrokerLogo
          companyName={account.companyName}
          logoUrl={account.brokerLogoUrl}
          className="w-9 h-9"
          logoClassName="w-6 h-6"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-foreground truncate">
              {account.credentials.login}
            </span>
            <span
              className={`w-[7px] h-[7px] rounded-full shrink-0 ${
                isActive && isConnected ? "bg-green-500" : "bg-red-500"
              }`}
              aria-label={isActive && isConnected ? "Connected" : "Disconnected"}
            />
            <GatewayChip label={getGatewayLabel(account)} />
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {account.companyName} · {account.serverName}
          </div>
        </div>
        {/* ⋯ fallback for pointer/mouse platforms (web/desktop) — subtle, no border */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            swipe.toggle();
          }}
          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-accent/40 shrink-0"
          aria-label="Account actions"
        >
          <MoreHorizontalIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * A trading account from the signed-in user's Vybe Trader profile not yet saved on this device —
 * the mobile twin of the desktop AccountsSheet's synced row. Tapping it connects it (the mint
 * round-trip materialises it as a local row); no edit/delete/swipe, the client portal owns those.
 */
function SyncedAccountRow({
  synced,
  isConnecting,
  onSelect,
}: {
  synced: SyncedAccountView;
  isConnecting: boolean;
  onSelect: () => void;
}) {
  const detail = [synced.mode, synced.currency].filter(Boolean).join(" · ");
  return (
    <div
      className="relative flex items-center gap-3 p-3 rounded-xl border border-border bg-card cursor-pointer hover:border-primary/50"
      onClick={() => {
        if (!isConnecting) onSelect();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !isConnecting) {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <BrokerLogo
        companyName={synced.gateway.companyName}
        logoUrl={gatewayLogoUrl(synced.gateway)}
        className="w-9 h-9"
        logoClassName="w-6 h-6"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm text-foreground truncate">{synced.login}</span>
          <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 h-4 text-[10px] leading-none text-secondary-foreground shrink-0">
            <ShieldCheck className="h-2.5 w-2.5" />
            VYBE
          </span>
        </div>
        <div className="text-xs text-muted-foreground truncate mt-0.5">
          {synced.gateway.companyName} · {synced.gateway.serverName}
          {detail && ` · ${detail}`}
        </div>
      </div>
      {isConnecting ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
      ) : (
        <span className="text-[10px] text-muted-foreground/60 shrink-0">Synced</span>
      )}
    </div>
  );
}

export default function AccountSelectorScreen({
  onBack,
  initialAddAccount = false,
}: AccountSelectorScreenProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(initialAddAccount);
  const [editingAccount, setEditingAccount] = useState<TradingAccount | null>(null);

  // Get specific data from store to minimize rerenders
  const accounts = useAccountsStore((state) => state.accounts);
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  const setActiveAccountId = useAccountsStore((state) => state.setActiveAccountId);
  const deleteAccount = useAccountsStore((state) => state.deleteAccount);

  // Get connection status
  const isConnected = useConnectionProgressStore((state) => state.isConnected);

  // Internal account sync — the same profile-owned rows the desktop AccountsSheet shows. Mounting
  // this screen is the mobile "panel open" moment, so refresh right then (backstop for a missed
  // presence push and for changes made directly on the engine).
  const syncedItems = useSyncedAccountsStore((state) => state.items);
  const syncedStatus = useSyncedAccountsStore((state) => state.status);
  const syncedError = useSyncedAccountsStore((state) => state.error);
  const connectingLogin = useSyncedAccountsStore((state) => state.connectingLogin);
  const connectSynced = useSyncedAccountsStore((state) => state.connect);
  const refreshSynced = useSyncedAccountsStore((state) => state.refresh);
  useEffect(() => {
    void refreshSynced();
  }, [refreshSynced]);

  // Deduped by login: an account also added by hand shows once, as the user's own editable row.
  const unmatchedSynced = useMemo(
    () =>
      selectUnmatchedSynced(
        syncedItems,
        new Set(accounts.map((a) => a.credentials.login)),
      ),
    [syncedItems, accounts],
  );

  const handleSelectSynced = async (login: number) => {
    try {
      await connectSynced(login);
      onBack();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not connect that account.");
    }
  };

  const handleSelectAccount = (accountId: string) => {
    if (accountId === activeAccountId && isConnected) {
      setShowAccountDetails(true);
      return;
    }

    setActiveAccountId(accountId);
    onBack();
  };

  const handleDeleteAccount = (accountId: string) => {
    deleteAccount(accountId);
    setShowDeleteConfirm(null);
  };

  if (showAccountDetails) {
    return <AccountDetailsScreen onBack={() => setShowAccountDetails(false)} />;
  }

  if (showAddAccount) {
    return (
      <AddAccountContent
        onBack={() => setShowAddAccount(false)}
        onAccountAdded={() => setShowAddAccount(false)}
      />
    );
  }

  if (editingAccount) {
    return (
      <EditAccountContent
        key={editingAccount.id}
        account={editingAccount}
        onBack={() => setEditingAccount(null)}
      />
    );
  }

  const connectedCount = activeAccountId && isConnected ? 1 : 0;

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
          <FaChevronLeft className="text-blue-400 text-xl" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Select Account</h1>
        <Button variant="ghost" size="icon" onClick={() => setShowAddAccount(true)} className="h-10 w-10">
          <PlusIcon className="h-5 w-5 text-blue-400" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* The sync answered but its trading server didn't — say so rather than silently showing
            fewer accounts than the user owns; local rows below are unaffected. */}
        {syncedStatus === "unavailable" && syncedError && (
          <div className="px-5 pt-3">
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40">
              <AlertDescription className="text-xs leading-snug">
                {syncedError} Your saved accounts are still available.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Account List */}
        {accounts.length === 0 && unmatchedSynced.length === 0 && syncedStatus === "loading" ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="h-6 w-6 text-muted-foreground/60 animate-spin mb-2" />
            <p className="text-xs text-muted-foreground">Loading your accounts…</p>
          </div>
        ) : accounts.length === 0 && unmatchedSynced.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-8">
            <div className="text-center mb-8">
              <PlusIcon className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground text-lg mb-2">
                No trading accounts added yet
              </p>
              <p className="text-muted-foreground/70 text-sm">
                Add your first account to get started
              </p>
            </div>
            <Button onClick={() => setShowAddAccount(true)} className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              Add Account
            </Button>
          </div>
        ) : (
          <div className="px-5 py-3">
            <div className="space-y-1.5">
              {accounts.map((account) => (
                <div key={account.id}>
                  <AccountRow
                    account={account}
                    isActive={account.id === activeAccountId}
                    isConnected={isConnected}
                    onSelect={() => handleSelectAccount(account.id)}
                    onEdit={() => setEditingAccount(account)}
                    onDelete={() => setShowDeleteConfirm(account.id)}
                  />

                  {/* Delete Confirmation */}
                  {showDeleteConfirm === account.id && (
                    <div className="px-3 py-3">
                      <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                        <AlertDescription>
                          <p className="text-sm mb-3">
                            Are you sure you want to delete this account?
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteAccount(account.id)}
                              className="flex-1"
                            >
                              Delete
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(null)}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>
              ))}

              {/* Profile-owned accounts not yet on this device (read-only, tap to connect) */}
              {unmatchedSynced.map((synced) => (
                <SyncedAccountRow
                  key={`synced-${synced.login}`}
                  synced={synced}
                  isConnecting={connectingLogin === synced.login}
                  onSelect={() => void handleSelectSynced(synced.login)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary footer — "N accounts · N connected" */}
      {accounts.length + unmatchedSynced.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-border font-mono text-[10.5px] text-muted-foreground">
          <span>
            {accounts.length + unmatchedSynced.length} account
            {accounts.length + unmatchedSynced.length !== 1 ? "s" : ""} ·{" "}
            {connectedCount} connected
          </span>
        </div>
      )}
    </div>
  );
}
